import { ArrowRight, CheckCircle, Clock, Warning } from '@phosphor-icons/react';
import { useState, useEffect, useMemo } from 'react';
import { RequestDocument } from '../../../../../signup/customers/DocumentUpload';
import { useUser } from '../../../../../../utils/UserContext';
import { Application } from '../../../../../../types/dbTypes';
import {
  ApplicationStep,
  CustomerWithDriverOnboarding,
} from '../../../../../../types/apiTypes';
import {
  isOnboardingStatementsContext,
  ONBOARDING_STATEMENTS_UPLOAD_COPY,
} from '../utils/onboarding';

/**
 * OnboardingStepsWidget
 *
 * Enterprise-grade onboarding component that guides users through account setup.
 *
 * Design Philosophy:
 * - Uses a gradient progress bar instead of numbered steps to reduce cognitive load
 * - Focuses user attention on the current task rather than total remaining steps
 * - Shows percentage completion for transparency without overwhelming users
 * - Progressive disclosure: one step at a time
 *
 * Features:
 * - Clear visual hierarchy and progress tracking
 * - Accessible design with ARIA support
 * - Responsive layout for all screen sizes
 * - Configurable steps and callbacks
 * - Professional animations and transitions
 * - Loading states and error handling ready
 *
 * Usage:
 * ```tsx
 * // Demo/Test Mode (default) - Click through steps automatically
 * <OnboardingStepsWidget />
 *
 * // Production Mode - Connect to your backend
 * <OnboardingStepsWidget
 *   enableTestMode={false}
 *   currentStepIndex={userProgress.stepIndex}
 *   onStepAction={(stepId, index) => {
 *     // Handle step action (e.g., open Plaid, navigate, etc.)
 *     handleStepAction(stepId);
 *   }}
 *   isLoading={isProcessing}
 *   isComplete={userProgress.isComplete}
 * />
 * ```
 */

// ============================================================================
// Types & Interfaces
// ============================================================================

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon?: React.ReactNode;
  estimatedTime?: string;
  ctaText?: string;
  ctaAction?: () => void;
}

interface OnboardingStepsWidgetProps {
  /** Current step index (0-based) */
  currentStepIndex?: number;
  /** Array of onboarding steps to display */
  steps?: OnboardingStep[];
  /** Callback when user clicks continue on a step */
  onStepAction?: (stepId: string, stepIndex: number) => void;
  /** Whether the widget is in loading state */
  isLoading?: boolean;
  /** Callback when all steps are completed */
  onComplete?: () => void;
  /** Custom completion message */
  completionMessage?: string;
  /** Enable test/demo mode - allows clicking through steps without callbacks. Defaults to true for easy testing. */
  enableTestMode?: boolean;
  /** Application step from the customer's application (e.g., 'plaid_link', 'final_consent') */
  application_step?: string;

  requestedDocs: RequestDocument[];

  application: Application;

  /** THT-703: switch from the onboarding bank-statement upload back to linking with Plaid. */
  onSwitchToPlaid?: () => void;
}

// ============================================================================
// Constants
// ============================================================================

const TACH_GREEN = '#6A9E68';
const TACH_GREEN_LIGHT = '#F0F5F0';
const TACH_GREEN_HOVER = '#5a8558';

// Map ApplicationStep enum to onboarding steps
const APPLICATION_STEP_TO_ONBOARDING_STEP: Record<string, OnboardingStep> = {
  plaid_link: {
    id: 'plaid_link',
    title: 'Verify Your Business Revenue',
    description:
      "Link your bank instantly with Plaid, or submit 6 months of bank statements — whichever is easiest. To open your account we don't pull your personal or business credit, and we never require a personal guarantee.",
    estimatedTime: '1-2 minutes',
    ctaText: 'Verify Revenue',
  },
  plaid_link_retry: {
    id: 'plaid_link_retry',
    title: 'Retry Bank Account Connection',
    description:
      'We need to re-verify your bank account connection. Please link your account again through our secure partner, Plaid.',
    estimatedTime: '1-2 minutes',
    ctaText: 'Reconnect Bank Account',
  },
  manual_document_upload: {
    id: 'manual_document_upload',
    title: 'Upload Required Documents',
    description:
      'Our compliance team needs a few additional documents to complete your application. All uploads are secure and encrypted.',
    estimatedTime: '2-3 minutes',
    ctaText: 'Upload Documents',
  },
  pending_review: {
    id: 'pending_review',
    title: 'Nice work — your application is under review',
    description:
      "Our team is reviewing your operating record and running a few standard checks. This usually takes 1–2 business days. We'll email you as soon as you're approved, and an account manager will reach out if any further action is required.",
    // No ctaText = no button will be shown
  },
};

// THT-703: onboarding "Submit Bank Statements" voice — shown on the manual_document_upload step only
// when the sole open request is the onboarding one (otherwise we keep the generic compliance voice
// above so an admin doc request isn't mislabeled).
const ONBOARDING_STATEMENTS_STEP: OnboardingStep = {
  id: ApplicationStep.ManualDocumentUpload,
  title: ONBOARDING_STATEMENTS_UPLOAD_COPY.title,
  description: ONBOARDING_STATEMENTS_UPLOAD_COPY.description,
  estimatedTime: '2-3 minutes',
  ctaText: 'Upload Statements',
};

// Default steps configuration (fallback when no application_step provided)
// Note: These are for the post-activation onboarding steps widget
const DEFAULT_STEPS: OnboardingStep[] = [
  {
    id: 'link-bank-account',
    title: 'Verify Your Business Revenue',
    description:
      'Link your bank instantly with Plaid, or submit 6 months of bank statements — whichever is easiest. This helps us verify your business revenue.',
    estimatedTime: '1-2 minutes',
    ctaText: 'Verify Revenue',
  },
  {
    id: 'add-assets',
    title: 'Add Your Fleet Information',
    description:
      'Provide details about your trucks and trailers. This helps us accurately assess your business and provide tailored services.',
    estimatedTime: '3-5 minutes',
    ctaText: 'Add Fleet Info',
  },
  {
    id: 'upload-documents',
    title: 'Upload Required Documents',
    description:
      'Our compliance team needs a few additional documents to complete your application. All uploads are secure and encrypted.',
    estimatedTime: '2-3 minutes',
    ctaText: 'Upload Documents',
  },
];

// ============================================================================
// Component
// ============================================================================

function OnboardingStepsWidget({
  currentStepIndex = 0,
  steps = DEFAULT_STEPS,
  onStepAction,
  isLoading = false,
  onComplete,
  completionMessage = 'Your account setup is complete! We will notify you when your account is activated.',
  enableTestMode = true, // Default to true for easy testing
  application_step,
  requestedDocs,
  application,
  onSwitchToPlaid,
}: OnboardingStepsWidgetProps) {
  // Local state for demo/test mode
  const [localStepIndex, setLocalStepIndex] = useState(currentStepIndex);
  const [isVisible, setIsVisible] = useState(false);
  const { userRecord } = useUser();
  const isComplete =
    (userRecord as CustomerWithDriverOnboarding)?.status === 'active';

  // THT-703: the onboarding statements voice + "Switch to Plaid" show only when the sole open request
  // is the onboarding bank-statement one. If an admin request is also open (or is the only one), we
  // fall back to the generic compliance voice and hide the switch.
  const isOnboardingStatements = useMemo(
    () => isOnboardingStatementsContext(application_step, requestedDocs),
    [application_step, requestedDocs]
  );

  // Determine the current step based on application_step
  const currentStep = useMemo(() => {
    if (isOnboardingStatements) {
      return ONBOARDING_STATEMENTS_STEP;
    }
    if (
      application_step &&
      APPLICATION_STEP_TO_ONBOARDING_STEP[application_step]
    ) {
      return APPLICATION_STEP_TO_ONBOARDING_STEP[application_step];
    }
    // Fallback to default steps behavior for test mode
    const activeStepIndex = enableTestMode ? localStepIndex : currentStepIndex;
    return steps[activeStepIndex];
  }, [
    isOnboardingStatements,
    application_step,
    enableTestMode,
    localStepIndex,
    currentStepIndex,
    steps,
  ]);

  // Use local state if in test mode, otherwise use prop
  const activeStepIndex = enableTestMode ? localStepIndex : currentStepIndex;

  // Calculate progress (for test mode)
  const progressPercentage = useMemo(() => {
    const docRequestsComplete =
      requestedDocs?.length > 0 &&
      !requestedDocs.some((item) => item.submitted === false);
    const plaidComplete =
      application?.was_revenue_verified_by_plaid ||
      application_step !== ApplicationStep.PlaidLink;
    if (application_step === ApplicationStep.PendingReview) {
      return 100;
    } else if (application_step === ApplicationStep.PlaidLinkRetry) {
      return 85;
    } else if (docRequestsComplete) {
      return 75;
    } else if (
      !docRequestsComplete &&
      application_step === ApplicationStep.ManualDocumentUpload
    ) {
      return 65;
    } else if (plaidComplete) {
      return 50;
    } else if (application_step === ApplicationStep.PlaidLink) {
      // When using application_step, show minimal progress
      return 0;
    }

    return (activeStepIndex / steps.length) * 100;
  }, [
    activeStepIndex,
    steps.length,
    application_step,
    application,
    requestedDocs,
  ]);

  // Entrance animation
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleStepAction = () => {
    if (isLoading) return;

    // If application_step is provided, always call the callback (production mode)
    if (application_step && currentStep && onStepAction) {
      onStepAction(currentStep.id, 0);
      return;
    }

    if (enableTestMode) {
      // Test/Demo mode: automatically advance to next step
      if (activeStepIndex < steps.length - 1) {
        setLocalStepIndex(activeStepIndex + 1);
      } else {
        // Trigger completion
        setLocalStepIndex(steps.length);
      }
    } else {
      // Production mode: call callback
      if (currentStep && onStepAction) {
        onStepAction(currentStep.id, activeStepIndex);
      }
    }
  };

  // ============================================================================
  // Render: Completion State
  // ============================================================================

  if (isComplete || activeStepIndex >= steps.length) {
    return (
      <div
        className={`w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-all duration-500 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
        }`}
        role="status"
        aria-live="polite"
      >
        {/* Success Progress Bar - 100% Complete */}
        {/* <div className="relative h-2 w-full overflow-hidden bg-gray-100">
          <div
            className="relative h-full transition-all duration-700 ease-out"
            style={{
              width: '100%',
              background: `linear-gradient(90deg, ${TACH_GREEN} 0%, ${TACH_GREEN_HOVER} 100%)`,
            }}
          >

            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 animate-pulse" />
          </div>
        </div> */}

        <div className="p-8">
          {/* Success Icon & Message */}
          <div className="flex items-start gap-4">
            <div
              className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full"
              style={{ backgroundColor: TACH_GREEN_LIGHT }}
            >
              <CheckCircle
                weight="fill"
                size={32}
                style={{ color: TACH_GREEN }}
              />
            </div>

            <div className="flex flex-1 flex-col gap-3">
              <div>
                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  Setup Complete
                </h3>
                <p className="text-base leading-relaxed text-gray-600">
                  {completionMessage}
                </p>
              </div>

              {/* Next Steps Info */}
              <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <Clock
                  size={20}
                  className="mt-0.5 flex-shrink-0 text-gray-500"
                />
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    What happens next?
                  </p>
                  <p className="mt-1 text-sm text-gray-600">
                    Our team is reviewing the signed documents and sharing them
                    with our bank partners. Your account will be activated
                    shortly, upon final approval.
                  </p>
                </div>
              </div>

              {onComplete && (
                <button
                  onClick={onComplete}
                  className="mt-2 self-start text-sm font-medium text-gray-600 underline-offset-2 transition-colors hover:text-gray-900 hover:underline"
                >
                  Return to Dashboard
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // Render: Active Step
  // ============================================================================

  return (
    <div
      className={`w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-all duration-500 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
      }`}
      role="region"
      aria-label="Account setup progress"
    >
      {/* Enhanced Progress Bar */}
      <div className="relative h-2 w-full overflow-hidden bg-gray-100">
        {/* Gradient Progress Fill */}
        <div
          className="relative h-full transition-all duration-700 ease-out"
          style={{
            width: `${progressPercentage}%`,
            background: `linear-gradient(90deg, ${TACH_GREEN} 0%, ${TACH_GREEN_HOVER} 100%)`,
          }}
          role="progressbar"
          aria-valuenow={progressPercentage}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${Math.round(progressPercentage)}% complete`}
        ></div>
      </div>

      <div className="p-6">
        {/* Header — hidden in pending review, where the "In Review" card is the whole story */}
        {currentStep?.id !== ApplicationStep.PendingReview && (
          <div className="mb-4">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Complete Your Account Setup
              </h3>
            </div>
            <p className="text-sm text-gray-600">
              Complete the required steps below to unlock full access to your
              account
            </p>
          </div>
        )}

        {/* Current Step Content */}
        {currentStep && (
          <div
            className={`relative rounded-lg border border-gray-200 p-6 ${
              currentStep.id === 'pending_review'
                ? 'bg-blue-50'
                : 'bg-yellow-50'
            }`}
          >
            {/* Status Badge */}
            <div className="absolute -top-2 right-4 z-10">
              {currentStep.id === 'pending_review' ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-500 px-3 py-1.5 text-xs font-semibold text-white shadow-md">
                  <Clock size={14} weight="fill" />
                  In Review
                </span>
              ) : (
                <span className="inline-flex animate-pulse items-center gap-1.5 rounded-full bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white shadow-md">
                  <Warning size={14} weight="fill" />
                  Required
                </span>
              )}
            </div>

            {/* Step Header */}
            <div className="mb-4">
              <h4 className="mb-3 text-xl font-bold text-gray-900 md:text-2xl">
                {currentStep.title}
              </h4>
              <p className="text-base leading-relaxed text-gray-700">
                {currentStep.description}
              </p>
            </div>

            {/* Metadata */}
            {currentStep.estimatedTime && (
              <div className="mb-6 flex items-center gap-2 text-sm text-gray-600">
                <Clock size={16} className="text-gray-500" />
                <span>Estimated time: {currentStep.estimatedTime}</span>
              </div>
            )}

            {/* CTA Button - Only show if ctaText is provided */}
            {currentStep.ctaText && (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <button
                  onClick={handleStepAction}
                  disabled={isLoading}
                  className="group inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 font-semibold text-white shadow-sm transition-all duration-200 hover:shadow-md active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:shadow-sm"
                  style={{
                    backgroundColor: isLoading ? '#9CA3AF' : TACH_GREEN,
                  }}
                  onMouseEnter={(e) => {
                    if (!isLoading) {
                      e.currentTarget.style.backgroundColor = TACH_GREEN_HOVER;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isLoading) {
                      e.currentTarget.style.backgroundColor = TACH_GREEN;
                    }
                  }}
                  aria-busy={isLoading}
                >
                  {isLoading ? (
                    <>
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      <span>Loading...</span>
                    </>
                  ) : (
                    <>
                      <span>{currentStep.ctaText}</span>
                      <ArrowRight
                        size={20}
                        weight="bold"
                        className="transition-transform duration-200 group-hover:translate-x-0.5"
                      />
                    </>
                  )}
                </button>

                {/* THT-703: let customers who chose statements switch back to instant Plaid linking */}
                {isOnboardingStatements && onSwitchToPlaid && (
                  <button
                    onClick={onSwitchToPlaid}
                    disabled={isLoading}
                    className="text-sm font-medium text-gray-600 underline-offset-2 transition-colors hover:text-gray-900 hover:underline disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Prefer to link instantly? Switch to Plaid →
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Security Badge */}
        {/* <div className="mt-6 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
          <ShieldCheck size={20} className="flex-shrink-0 text-green-700" />
          <p className="text-sm text-green-800">
            <span className="font-semibold">Secure & Private:</span> Your
            information is encrypted and protected
          </p>
        </div> */}

        {/* Demo Mode Controls */}
        {/* {enableTestMode && (
          <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
            <button
              onClick={handleTestPrevious}
              disabled={activeStepIndex === 0}
              className="text-sm font-medium text-gray-600 transition-colors hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40"
            >
              ← Previous
            </button>
            <span className="text-xs text-gray-500">Demo Mode Active</span>
            <button
              onClick={handleTestReset}
              className="text-sm font-medium text-gray-600 transition-colors hover:text-gray-900"
            >
              Reset
            </button>
          </div>
        )} */}
      </div>
    </div>
  );
}

export default OnboardingStepsWidget;
